import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { Button, FormValidators } from 'app-core';
import { interval, Subject, take, takeUntil } from 'rxjs';
import { IBankAccount, IUsers } from '@alpha/types';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { DialogService } from 'app-shared-ui';
import { BankAccountService, CacheService, UserService } from 'app-service';

import ButtonType = Data.Core.ButtonType;
import IconType = Data.Core.IconType;

@Component({
  selector: 'app-bank-account',
  templateUrl: './bank-account.component.html',
  styleUrls: ['./bank-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [BankAccountService],
})
export class BankAccountComponent implements OnInit {
  public loading: boolean = false;
  public verificationForm: FormGroup;
  public accountControl = new FormControl('', [Validators.required]);
  public mobileOTP = new FormControl('', FormValidators.accountOTP);
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  private countdown = 120; // Initial countdown value
  private isExistingAccount: boolean = false;
  public verifyAccountButton: Button = new Button({
    label: 'Add Account',
    type: ButtonType.button,
    action: () => this.verifyAccount(),
    disabled: () => !this.verificationForm.valid,
  });

  @ViewChild('errorMsg', { static: true })
  public errorMsg!: TemplateRef<any>;
  @ViewChild('errorMsgInvalidToken', { static: true })
  public errorMsgInvalidToken!: TemplateRef<any>;
  @ViewChild('successMsg', { static: true })
  public successMsg!: TemplateRef<any>;
  @ViewChild('otpMsg', { static: true })
  public otpMsg!: TemplateRef<any>;
  @ViewChild('successMsgHeader', { static: true })
  public successMsgHeader!: TemplateRef<any>;
  public selectedStep?: number;

  public resendOTPButton: Button = new Button({
    type: ButtonType.button,
    label: 'Send OTP',
    action: () => {
      const savedCountdown =
        this._cacheService.getItem<string>('resetOTPCountDown');
      if (!parseInt(savedCountdown || '', 10)) {
        this.generateBankAccountToken();
      }
      interval(1000) // Emit value every second
        .pipe(take(this.countdown)) // Limit the emissions to the countdown duration
        .subscribe({
          next: () => {
            if (this.countdown > 0) {
              this.resendOTPButton.disabled = () => true;
              this.countdown--; // Reduce countdown every second
              this.resendOTPButton.label = `${this.countdown}`;
              this._cacheService.setItem(
                'resetOTPCountDown',
                this.countdown.toString(),
              );
            }
          },
          complete: () => {
            this._cacheService.removeItem('resetOTPCountDown');
            setTimeout(() => {
              this.countdown = 120;
              this.resendOTPButton.label = 'Resend OTP';
              this.resendOTPButton.disabled = () => false;
            }, 2000);
          }, // Reset after 2 seconds
        });
    },
    icon: IconType.refreshIcon,
    disabled: () => !this.accountControl.valid,
  });

  public constructor(
    private readonly _bankAccountService: BankAccountService,
    private readonly _cacheService: CacheService,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _formBuilder: FormBuilder,
    private readonly _userService: UserService,
    private readonly _dialogService: DialogService,
    private readonly _router: Router,
  ) {
    this.verificationForm = this._formBuilder.group([
      this.accountControl,
      this.mobileOTP,
    ]);
  }

  public ngOnInit(): void {
    this.getAccountDetails();
  }

  private getAccountDetails(): void {
    this._bankAccountService
      .getBankAccountByUserId()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((item: IBankAccount) => {
        if (item && item!.account && item!.account!.id) {
          this.accountControl.patchValue(item.account.id);
          this.isExistingAccount = true;
        }
      });
  }

  private verifyAccount(): void {
    this.loading = true;
    const req: Partial<IBankAccount> = {
      account: {
        id: this.accountControl.value || '',
        token: {
          id: this.mobileOTP.value?.toString() || '',
          createdAt: new Date(),
        },
      },
    };

    if (this.isExistingAccount) {
      this._bankAccountService
        .updateBankAccount(req)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (item: IBankAccount) => {
            if (item.account.id) {
              this.accountControl.patchValue(item.account.id);
              this.isExistingAccount = true;
            }
            this._dialogService.openDialog({
              body: this.successMsg,
              header: this.successMsgHeader,
            });
          },
          (error) => {
            if (error.error.includes('Invalid Token')) {
              this._dialogService.openDialog({
                body: this.errorMsgInvalidToken,
              });
            } else {
              this._dialogService.openDialog({
                body: this.errorMsg,
              });
            }
            this.verificationForm.reset();
            this.loading = false;
          },
          () => {
            this.loading = false;
            this.verifyAccountButton.disabled = () => true;
            this._cdRef.markForCheck();
          },
        );
    } else {
      this._bankAccountService
        .addBankAccount(req)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (item: IBankAccount) => {
            if (item.account.id) {
              this.accountControl.patchValue(item.account.id);
              this.isExistingAccount = true;
            }
            this._dialogService.openDialog({
              body: this.successMsg,
              header: this.successMsgHeader,
            });
          },
          (error) => {
            if (error.error.includes('Invalid Token')) {
              this._dialogService.openDialog({
                body: this.errorMsgInvalidToken,
              });
            } else {
              this._dialogService.openDialog({
                body: this.errorMsg,
              });
            }
            this.verificationForm.reset();
            this.loading = false;
          },
          () => {
            this.loading = false;
            this.verifyAccountButton.disabled = () => true;
            this._cdRef.markForCheck();
          },
        );
    }
  }

  public generateBankAccountToken(): void {
    this.loading = true;
    this._bankAccountService
      .generateBankAccountToken()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this._dialogService.openDialog({
            body: this.otpMsg,
            header: this.successMsgHeader,
          });
        },
        () => {
          this._dialogService.openDialog({
            body: this.errorMsg,
          });
          this.verificationForm.reset();
          this.loading = false;
        },
        () => {
          this.loading = false;
          this._cdRef.markForCheck();
        },
      );
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
